import React from 'react';
import { 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Avatar, 
  Box,
  IconButton,
  Tooltip
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InfoIcon from '@mui/icons-material/Info';

export default function SummaryCards({ selectedEvent, dashboardMetrics, submissions }) {
  // Calculate real metrics from props
  const getSummaryStats = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    
    // Today's submissions
    const todaySubmissions = submissions.filter(s => {
      const submissionDate = new Date(s.createdAt);
      return submissionDate.toDateString() === today.toDateString();
    }).length;
    
    // Yesterday's submissions for comparison
    const yesterdaySubmissions = submissions.filter(s => {
      const submissionDate = new Date(s.createdAt);
      return submissionDate.toDateString() === yesterday.toDateString();
    }).length;
    
    // Calculate growth
    const growthPercent = yesterdaySubmissions > 0
      ? Math.round(((todaySubmissions - yesterdaySubmissions) / yesterdaySubmissions) * 100)
      : null; // render as 'New' below
    
    // Hot leads (New submissions from last 24 hours)
    const hotLeads = submissions.filter(s => {
  // Check if submission has insuranceTypes array
  if (!s.insuranceTypes || !Array.isArray(s.insuranceTypes)) {
    return false;
  }
  
  // Count unique insurance types for this submission
  const uniqueInsuranceTypes = new Set();
  s.insuranceTypes.forEach(insurance => {
    const type = typeof insurance === 'string' ? insurance : insurance.type;
    if (type) {
      uniqueInsuranceTypes.add(type.toLowerCase());
    }
  });
  
  // Return true if lead has 2 or more different insurance types
  return uniqueInsuranceTypes.size >= 2;
}).length;
    
    // Upcoming renewals (if you have renewal date tracking)
    const upcomingRenewals = submissions.filter(s => {
      if (!s.insuranceTypes || !Array.isArray(s.insuranceTypes)) return false;
      
      return s.insuranceTypes.some(insurance => {
        if (!insurance.renewalDate) return false;
        const renewalDate = new Date(insurance.renewalDate);
        const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
        return renewalDate <= thirtyDaysFromNow && renewalDate >= today;
      });
    }).length;

    return [
      {
        label: 'Total Visitors',
        value: selectedEvent?.totalVisitors || dashboardMetrics?.totalSubmissions || 0,
        icon: <PersonIcon fontSize="medium" />,
        color: '#e3f2fd',
        iconColor: '#2196f3',
        subtext: selectedEvent ? 'Event attendees' : 'Total submissions',
        change: growthPercent,
        changeText: growthPercent === null
            ? 'New'
            : `${growthPercent >= 0 ? '+' : ''}${growthPercent}% from yesterday`
      },
      {
        label: 'Conversion Rate',
        value: `${dashboardMetrics?.conversionRate || 0}%`,
        icon: <TrendingUpIcon fontSize="medium" />,
        color: '#e8f5e8',
        iconColor: '#4caf50',
        subtext: 'Leads converted to clients',
        change: null,
        changeText: `${dashboardMetrics?.contractedCount || 0} of ${dashboardMetrics?.totalSubmissions || 0} contracted`
      },
      {
        label: 'Upcoming Renewals',
        value: upcomingRenewals,
        icon: <AccessAlarmIcon fontSize="medium" />,
        color: '#fff9e1',
        iconColor: '#ffd600',
        subtext: 'Policies expiring soon',
        urgent: upcomingRenewals > 0,
        change: null,
        changeText: 'Next 30 days'
      },
      {
        label: 'Hot Leads',
        value: hotLeads,
        icon: <WhatshotIcon fontSize="medium" />,
        color: '#ffeaea',
        iconColor: '#f44336',
        subtext: 'Multi‑interest leads',
        urgent: hotLeads > 5,
        change: null,
        changeText: 'Require follow-up'
      }
    ];
  };

  const summaryStats = getSummaryStats();

  return (
    <Grid container spacing={2}>
      {summaryStats.map((card, index) => (
        <Grid item xs={12} sm={6} lg={3} key={card.label}>
          <Card 
            elevation={0} 
            sx={{
              background: `linear-gradient(135deg, ${card.color} 0%, ${card.color}dd 100%)`,
              borderRadius: 3,
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              border: '1px solid rgba(255,255,255,0.8)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                '& .card-icon': {
                  transform: 'scale(1.05)',
                }
              },
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                background: `linear-gradient(90deg, ${card.iconColor}, ${card.iconColor}aa)`
              }
            }}
          >
            <CardContent sx={{ p: 2.5, pb: 2 }}>
              {/* Horizontal Layout */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: 2
              }}>
                {/* Icon */}
                <Avatar 
                  className="card-icon"
                  sx={{
                    bgcolor: card.iconColor,
                    color: '#fff',
                    width: 48,
                    height: 48,
                    boxShadow: `0 3px 12px ${card.iconColor}40`,
                    transition: 'all 0.3s ease',
                    flexShrink: 0
                  }}
                >
                  {card.icon}
                </Avatar>
                
                {/* Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      sx={{ 
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem'
                      }}
                    >
                      {card.label}
                    </Typography>
                    
                    {card.urgent && (
                      <Tooltip title="Requires attention" placement="top">
                        <IconButton 
                          size="small" 
                          sx={{ 
                            color: '#ff9800',
                            backgroundColor: 'rgba(255, 152, 0, 0.1)',
                            width: 24,
                            height: 24,
                            '&:hover': {
                              backgroundColor: 'rgba(255, 152, 0, 0.2)',
                            }
                          }}
                        >
                          <InfoIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 800, 
                      lineHeight: 1, 
                      mb: 0.5,
                      color: card.iconColor,
                      fontSize: '2rem'
                    }}
                  >
                    {card.value}
                  </Typography>
                  
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      fontWeight: 500,
                      lineHeight: 1.2,
                      fontSize: '0.75rem',
                      mb: 0.5
                    }}
                  >
                    {card.subtext}
                  </Typography>

                  {/* Change indicator */}
                  {card.change !== null && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: card.change >= 0 ? '#4caf50' : '#f44336',
                        fontWeight: 600,
                        fontSize: '0.7rem'
                      }}
                    >
                      {card.changeText}
                    </Typography>
                  )}
                  
                  {card.change === null && card.changeText && (
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.7rem'
                      }}
                    >
                      {card.changeText}
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}